/**
 * The printer portal's board filters (prototype PortalBoard F1): window, Any
 * Ship By, Any Work On Date, Blanks Late, Has Issue, Sort: Ship By. Kept in
 * the URL so a filtered board survives a reload; window and sort are not
 * filters and Clear Filters keeps them.
 */
import { DEFAULT_BOARD_WINDOW, parseBoardWindow } from "../../SupplierOrders/lib/boardWindow.ts";
import type { PortalShipByFilter, PortalSort, PortalWorkOnFilter, PrinterPortalFilters } from "../types.ts";

export const DEFAULT_PORTAL_FILTERS: PrinterPortalFilters = {
  window: DEFAULT_BOARD_WINDOW,
  search: "",
  shipBy: "any",
  workOn: "any",
  blanksLate: false,
  hasIssue: false,
  sort: "ship_by",
};

/* The API's values (printer-portal.validation `filtersSchema`). */
const SHIP_BY: readonly PortalShipByFilter[] = ["any", "overdue", "this_week", "none"];
const WORK_ON: readonly PortalWorkOnFilter[] = ["any", "planned", "not_planned"];
const SORTS: readonly PortalSort[] = ["ship_by", "deadline", "newest"];

const pick = <T extends string>(allowed: readonly T[], raw: string | null, fallback: T): T =>
  allowed.includes(raw as T) ? (raw as T) : fallback;

export function parsePortalFilters(params: URLSearchParams): PrinterPortalFilters {
  return {
    window: parseBoardWindow(params.get("window")),
    search: params.get("q") ?? "",
    shipBy: pick(SHIP_BY, params.get("shipBy"), "any"),
    workOn: pick(WORK_ON, params.get("workOn"), "any"),
    blanksLate: params.get("blanksLate") === "1",
    hasIssue: params.get("hasIssue") === "1",
    sort: pick(SORTS, params.get("sort"), "ship_by"),
  };
}

/** Only non-default values go in the URL; other params (tab) are kept. */
export function serializePortalFilters(filters: PrinterPortalFilters, base?: URLSearchParams): URLSearchParams {
  const params = new URLSearchParams(base);
  const set = (key: string, value: string | null) => (value === null ? params.delete(key) : params.set(key, value));
  set("window", filters.window === DEFAULT_BOARD_WINDOW ? null : filters.window);
  set("q", filters.search.trim() ? filters.search : null);
  set("shipBy", filters.shipBy === "any" ? null : filters.shipBy);
  set("workOn", filters.workOn === "any" ? null : filters.workOn);
  set("blanksLate", filters.blanksLate ? "1" : null);
  set("hasIssue", filters.hasIssue ? "1" : null);
  set("sort", filters.sort === "ship_by" ? null : filters.sort);
  return params;
}

export function hasActiveFilters(filters: PrinterPortalFilters): boolean {
  return (
    filters.search.trim() !== "" ||
    filters.shipBy !== "any" ||
    filters.workOn !== "any" ||
    filters.blanksLate ||
    filters.hasIssue
  );
}

/**
 * Query params for the lane and count reads, in the API's names: the window
 * always, the rest only when set.
 */
export function filterParams(filters: PrinterPortalFilters): Record<string, string> {
  const params: Record<string, string> = { window: filters.window };
  if (filters.search.trim()) params.search = filters.search.trim();
  if (filters.shipBy !== "any") params.shipBy = filters.shipBy;
  if (filters.workOn !== "any") params.workOn = filters.workOn;
  if (filters.blanksLate) params.blanksLate = "true";
  if (filters.hasIssue) params.hasIssue = "true";
  if (filters.sort !== "ship_by") params.sort = filters.sort;
  return params;
}

const addDays = (day: string, days: number) => {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

/**
 * The toolbar's filters on the Schedule's cards (the schedule read is not
 * filtered). Same rules as the API's board (printer-portal.rules
 * `matchesFilters`), against the server's `today`.
 */
export function matchesPortalFilters(
  card: { label: string; title: string; shipBy: string | null; workOn: string | null; blanksLate: boolean; issueOpen?: boolean; lane: string; items: { code: string; name: string }[] },
  filters: PrinterPortalFilters,
  today: string,
): boolean {
  const q = filters.search.trim().toLowerCase();
  if (q) {
    const hay = [card.label, card.title, ...card.items.map((item) => `${item.code} ${item.name}`)].join(" ").toLowerCase();
    if (!hay.includes(q)) return false;
  }
  const open = ["blanks_in_transit", "blanks_received", "in_production", "shipping"].includes(card.lane);
  if (filters.shipBy === "overdue" && !(card.shipBy && card.shipBy < today && open)) return false;
  if (filters.shipBy === "this_week" && !(card.shipBy && card.shipBy >= today && card.shipBy <= addDays(today, 7))) return false;
  if (filters.shipBy === "none" && card.shipBy) return false;
  if (filters.workOn === "planned" && !card.workOn) return false;
  if (filters.workOn === "not_planned" && card.workOn) return false;
  if (filters.blanksLate && !card.blanksLate) return false;
  if (filters.hasIssue && !card.issueOpen) return false;
  return true;
}
